'use client';

import { useLayoutEffect, useRef, useMemo } from 'react';
import Image from 'next/image';
import { CallToActionButton, LinkWithIcon } from '@/components/shared/CallToActionButton';

import { cn } from '@/app/utils/cn';
import { DotCardIcon } from '@/components/content-types/dotCardIcon';
import { DotBlockEditor } from '../shared/decodeTextBlock';
import { decode } from 'html-entities';

const DOTCMS_CONTENT_URL_REGEX =
    /^\/content\.[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

const WHITE_LINK_CLASSES =
    'text-white border-white not-prose hover:bg-white hover:!text-section-color';

const BODY_CLASSES_BLACK = cn(
    'prose prose-p:text-black prose-p:opacity-80 prose-p:text-sm prose-a:text-sm',
    'prose-a:text-black prose-a:opacity-80 prose-li:text-black prose-li:opacity-80',
    'prose-li:text-sm marker:text-black marker:opacity-80'
);

function renderCallToActionButtons(showCallToAction, callToAction, customStyles = '') {
    if (!showCallToAction || !callToAction) return null;

    return callToAction.map((cta, i) => {
        if (!cta) return null;

        const extraClass =
            (cta.buttonType === 'secondary' || cta.buttonType === 'link') &&
            !!cta.noPadding === false
                ? '!px-[10px] hover:border-gray-400 hover:border-[2px]'
                : '!px-0';

        const buttonProps = i !== 0 ? { ...cta, url: cta.url } : cta;

        return (
            <div key={(cta.identifier || '') + i}>
                <CallToActionButton
                    className={cn(
                        '!mx-0 !items-start !justify-start !bg-transparent !text-left text-[14px] font-[500] leading-[24px] !text-white no-underline transition-colors duration-300 ease-in-out hover:!text-gray-400 md:text-[18px]',
                        extraClass,
                        customStyles
                    )}
                    buttonType={cta.buttonType}
                    {...buttonProps}>
                    {cta.urlName || cta.title}
                </CallToActionButton>
            </div>
        );
    });
}

function shouldShowCallToAction(button, checkRegex = false) {
    if (!button?.urlName || !button?.url) {
        return false;
    }

    if (checkRegex) {
        return !DOTCMS_CONTENT_URL_REGEX.test(button.url);
    }

    return true;
}

function hasFeatureText(feature) {
    const text =
        typeof feature?.body?.content?.[0]?.content?.[0]?.text === 'string'
            ? feature.body.content[0].content[0].text.trim()
            : '';
    return text.length > 0;
}

function hasFeatureHeadline(feature) {
    const headlineValue =
        typeof feature?.headline === 'string'
            ? feature.headline.trim()
            : typeof feature?.headline?.text === 'string'
              ? feature.headline.text.trim()
              : '';
    return headlineValue.length > 0;
}

function hasFeatureCallToAction(feature) {
    return Array.isArray(feature?.callToAction)
        ? feature.callToAction.some((cta) => {
              if (!cta || typeof cta !== 'object') return false;
              const url = typeof cta.url === 'string' ? cta.url.trim() : '';

              return url.length > 0 && !DOTCMS_CONTENT_URL_REGEX.test(url);
          })
        : false;
}

function isFeatureEmpty(feature) {
    if (!feature || typeof feature !== 'object' || Array.isArray(feature)) return true;

    const hasText = hasFeatureText(feature);
    const hasHeadline = hasFeatureHeadline(feature);
    const hasCallToAction = hasFeatureCallToAction(feature);

    return !hasText && !hasHeadline && !hasCallToAction;
}

function CustomHeading({ children, className, level = 3 }) {
    const HeadingTag = `h${level}`;
    return (
        <HeadingTag className={cn('mt-0 text-2xl dark:text-white', className)}>
            {children}
        </HeadingTag>
    );
}

function Heading(props) {
    if (!props.node?.content) {
        return null;
    }
    const [{ text }] = props.node.content;
    const HeadingTag = `h${props.node.attrs?.level || 3}`;

    return (
        <HeadingTag
            className="dark:text-white"
            style={{
                textAlign: props.node.attrs?.textAlign || 'left'
            }}>
            {decode(text)}
        </HeadingTag>
    );
}

function ListItem({ node: { content } }) {
    if (!content) {
        return null;
    }
    const [item] = content;
    const { content: itemContent } = item;
    const [{ text }] = itemContent;
    return <li>{decode(text)}</li>;
}

function Layout1(props) {
    const { body, headline, callToAction, icon } = props;
    const [button] = callToAction || [];
    const showCallToAction = shouldShowCallToAction(button);
    const classes = cn('max-w-none prose prose-img:mb-0 prose-h3:mt-2 h-full flex flex-col gap-6');

    return (
        <div className={classes}>
            {icon && <DotCardIcon icon={icon?.idPath ?? icon} alt={headline} />}
            <div>
                {headline && <CustomHeading>{headline}</CustomHeading>}
                {body && (
                    <DotBlockEditor
                        className={cn(
                            'prose prose-p:text-sm prose-p:text-slate-500 prose-a:text-sm prose-a:text-slate-500',
                            'prose-p:last:mb-0 prose-p:last:pb-0 prose-p:empty:mb-0 prose-p:empty:hidden'
                        )}
                        blocks={body}
                        customRenderers={{
                            listItem: ListItem,
                            heading: Heading
                        }}
                    />
                )}
            </div>
            {showCallToAction && button && (
                <CallToActionButton className="!leading-none !no-underline" {...button}>
                    {button.urlName}
                </CallToActionButton>
            )}
        </div>
    );
}

function Layout2({ body, headline, callToAction, icon }) {
    const [button] = callToAction || [];
    const showCallToAction = shouldShowCallToAction(button);
    const linkClasses = cn(WHITE_LINK_CLASSES);
    const bodyClasses = cn(
        'prose prose-p:text-white prose-p:opacity-80 prose-p:text-sm prose-a:text-sm',
        'prose-a:text-white prose-a:opacity-80 prose-li:text-white prose-li:opacity-80',
        'prose-li:text-sm marker:text-white marker:opacity-80'
    );

    return (
        <div className="prose relative max-w-none rounded-xl bg-section-color p-8 pb-60 pr-12 prose-h3:mt-2 prose-img:mb-0">
            {headline && <CustomHeading className="text-white">{headline}</CustomHeading>}
            {body && (
                <div className={bodyClasses}>
                    <DotBlockEditor
                        blocks={body}
                        customRenderers={{
                            listItem: ListItem,
                            heading: Heading
                        }}
                    />
                </div>
            )}
            {showCallToAction && button && (
                <CallToActionButton {...button} buttonType="link" className={linkClasses}>
                    {button.urlName}
                </CallToActionButton>
            )}
            {icon && (
                <Image
                    src={icon?.idPath ?? icon}
                    alt="icon"
                    width={220}
                    height={220}
                    sizes="220px"
                    className="absolute bottom-2 right-2 object-contain"
                />
            )}
        </div>
    );
}

function Layout3({ body, headline, callToAction, icon }) {
    const [button] = callToAction || [];
    const showCallToAction = shouldShowCallToAction(button);

    const classes = cn('max-w-none prose prose-img:mb-0 prose-h3:mt-2 bg-white p-8 rounded-xl');

    return (
        <div className={classes}>
            {icon && <DotCardIcon icon={icon?.idPath ?? icon} alt={headline} />}
            {headline && <CustomHeading className="dark:text-black">{headline}</CustomHeading>}
            {body && (
                <DotBlockEditor
                    className="prose prose-p:text-sm prose-p:text-slate-500 prose-a:text-sm prose-a:text-slate-500"
                    blocks={body}
                    customRenderers={{
                        listItem: ListItem,
                        heading: function (props) {
                            if (!props.content) {
                                return null;
                            }
                            const [{ text }] = props.content;
                            const HeadingTag = `h${props.attrs?.level || 3}`;

                            return (
                                <HeadingTag
                                    className="dark:text-black"
                                    style={{
                                        textAlign: props.attrs?.textAlign || 'left'
                                    }}>
                                    {decode(text)}
                                </HeadingTag>
                            );
                        }
                    }}
                />
            )}
            {showCallToAction && button && (
                <CallToActionButton {...button}>{button.urlName}</CallToActionButton>
            )}
        </div>
    );
}

function Layout4({ icon, headline, body, callToAction, identifier }) {
    const [button] = callToAction || [];

    const showCallToAction = shouldShowCallToAction(button);

    return (
        <div className="group col-span-1 flex flex-col gap-8 rounded-2xl bg-indigo-100 p-8 last:odd:col-span-full last:odd:md:flex-row last:odd:md:items-center">
            <div className="flex flex-1 flex-col gap-4">
                {icon && (
                    <div className="not-prose inline-block w-fit rounded-full bg-white p-4">
                        <Image
                            src={icon?.idPath ?? icon}
                            alt="icon"
                            width={64}
                            height={64}
                            className="object-contain"
                        />
                    </div>
                )}
                {headline && <CustomHeading className="dark:text-black">{headline}</CustomHeading>}
                {body && (
                    <DotBlockEditor
                        className="prose prose-p:text-sm prose-p:font-normal prose-p:text-blue-600 prose-p:opacity-80 prose-a:text-sm prose-a:font-normal prose-a:text-blue-600 prose-a:opacity-80"
                        blocks={body}
                        customRenderers={{
                            listItem: ListItem,
                            heading: Heading
                        }}
                    />
                )}
                {showCallToAction && (
                    <CallToActionButton {...button}>{button.urlName}</CallToActionButton>
                )}
            </div>
            {identifier && (
                <div className="relative aspect-video h-auto max-h-56 min-h-32 w-full flex-1">
                    <Image
                        src={identifier + '/image'}
                        alt="Feature Image"
                        fill={true}
                        className="object-cover"
                        sizes="(max-width: 400px) calc(100vw - 32px), 400px"
                    />
                </div>
            )}
        </div>
    );
}

function Layout5({ icon, headline, body, callToAction }) {
    const [button] = callToAction || [];
    const showCallToAction = shouldShowCallToAction(button);

    const linkClasses = cn(WHITE_LINK_CLASSES);
    const bodyClasses = cn(
        'first:border-fuschia-700 first:bg-fuschia-100 prose prose-p:text-black prose-p:opacity-80 prose-p:text-sm prose-a:text-sm prose-a:text-black prose-a:opacity-80 prose-li:text-black prose-li:opacity-80 prose-li:text-sm marker:text-black marker:opacity-80'
    );

    return (
        <div
            className={cn(
                'prose relative max-w-none rounded-xl border p-8 pr-12 prose-h3:mt-2 prose-img:mb-0'
            )}>
            {icon && <DotCardIcon icon={icon?.idPath ?? icon} alt={headline} />}
            {headline && <CustomHeading className="text-black">{headline}</CustomHeading>}
            {body && (
                <div className={bodyClasses}>
                    <DotBlockEditor
                        blocks={body}
                        customRenderers={{
                            listItem: ListItem,
                            heading: Heading
                        }}
                    />
                </div>
            )}
            {showCallToAction && button && (
                <CallToActionButton {...button} className={linkClasses}>
                    {button.urlName}
                </CallToActionButton>
            )}
        </div>
    );
}

function Layout6({ headline, body, callToAction }) {
    const [button] = callToAction || [];
    const showCallToAction = shouldShowCallToAction(button);

    return (
        <div
            className={cn(
                'prose relative max-w-none rounded-xl border !border-[#9712C3] !bg-[#FAF3FC] p-8 pr-12 prose-h3:mt-2 prose-img:mb-0'
            )}>
            {headline && <CustomHeading className="text-black">{headline}</CustomHeading>}
            {body && (
                <div className={BODY_CLASSES_BLACK}>
                    <DotBlockEditor
                        blocks={body}
                        customRenderers={{
                            listItem: ListItem,
                            heading: Heading
                        }}
                    />
                </div>
            )}
            {showCallToAction && button && (
                <CallToActionButton {...button}>{button.urlName}</CallToActionButton>
            )}
        </div>
    );
}

function Layout7({ headline, body, callToAction }) {
    const [button] = callToAction || [];
    const showCallToAction = shouldShowCallToAction(button);

    return (
        <div
            className={cn(
                'prose relative max-w-none rounded-xl border !border-[#9712C3] !bg-[#FAF3FC] p-8 pr-12 prose-h3:mt-2 prose-img:mb-0'
            )}>
            {headline && <CustomHeading className="text-black">{headline}</CustomHeading>}
            {body && (
                <div className={BODY_CLASSES_BLACK}>
                    <DotBlockEditor
                        blocks={body}
                        customRenderers={{
                            listItem: ListItem,
                            heading: Heading
                        }}
                    />
                </div>
            )}
            {showCallToAction && button && (
                <CallToActionButton className="!text-white !no-underline" {...button}>
                    {button.urlName}
                </CallToActionButton>
            )}
        </div>
    );
}

function Layout8({ globalHeadline, features }) {
    const bodyClasses = cn(
        'prose prose-p:text-white prose-p:opacity-80 prose-p:text-sm prose-a:text-sm',
        'prose-a:text-white prose-a:opacity-80 prose-a:no-underline prose-a:hover:no-underline',
        'prose-li:text-white prose-li:opacity-80 prose-li:text-sm marker:text-white marker:opacity-80'
    );

    return (
        <div
            className={cn(
                '!bg-blue-700 prose px-[24px] relative max-w-none prose-h3:mt-2 prose-img:mb-0',
                'prose-a:no-underline prose-a:hover:no-underline'
            )}>
            {globalHeadline && (
                <CustomHeading
                    level={3}
                    className="py-[32px] pb-[24px] text-center !text-4xl font-[500] !text-white md:pb-[72px] md:text-[64px]">
                    {globalHeadline}
                </CustomHeading>
            )}
            <div className="relative grid grid-cols-1 items-stretch gap-10 md:grid-cols-2 lg:grid-cols-3">
                {Array.isArray(features) &&
                    features.map((feature, index) => {
                        if (!feature || isFeatureEmpty(feature)) return null;

                        const [button] = feature.callToAction || [];
                        const showCallToAction = shouldShowCallToAction(button, true);
                        const buttonUrl = button?.url || '#';

                        const handleClick = (e) => {
                            if (e.target.closest('a, button')) {
                                return;
                            }
                            if (showCallToAction && buttonUrl && buttonUrl !== '#') {
                                window.location.href = buttonUrl;
                            }
                        };

                        const content = (
                            <div
                                key={feature.identifier || index}
                                onClick={showCallToAction ? handleClick : undefined}
                                className={cn(
                                    bodyClasses,
                                    'group mb-[16px] !no-underline rounded-[8px] !bg-blue-550',
                                    'p-[24px] last:mb-0 transition-colors duration-300 ease-in-out',
                                    'h-full w-full flex flex-col',
                                    showCallToAction && 'hover:!bg-blue-750 hover:cursor-pointer'
                                )}>
                                {feature.icon && (
                                    <div className="mb-4 flex size-[56px] items-center justify-center rounded-full bg-blue-700 transition-colors duration-300 ease-in-out group-hover:!bg-blue-550">
                                        <Image
                                            src={feature.icon?.idPath ?? feature.icon}
                                            alt={feature.alt || 'image'}
                                            height={38}
                                            width={38}
                                            sizes="38px"
                                            className="size-[38px]"
                                        />
                                    </div>
                                )}

                                {feature.headline && (
                                    <CustomHeading
                                        level={3}
                                        className="!text-4xl text-white md:text-[40px]">
                                        {feature.headline}
                                    </CustomHeading>
                                )}

                                {!feature.headline && <div className="h-[30px]"></div>}

                                {feature.body && (
                                    <div className="grow">
                                        <DotBlockEditor
                                            blocks={feature.body}
                                            customRenderers={{
                                                listItem: ListItem,
                                                heading: Heading
                                            }}
                                        />
                                    </div>
                                )}

                                {renderCallToActionButtons(showCallToAction, feature.callToAction)}
                            </div>
                        );

                        return (
                            <div key={feature.identifier || index} className="flex h-full">
                                {content}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}

function Layout9({ features }) {
    const bodyClasses = cn(
        'prose max-w-none prose-p:text-black prose-p:opacity-80 prose-p:text-sm prose-p:text-justify prose-a:text-sm',
        'prose-a:opacity-80 prose-a:no-underline prose-a:hover:no-underline',
        'prose-li:text-black prose-li:opacity-80 prose-li:text-sm marker:text-black marker:opacity-80'
    );

    const validFeaturesWithIndex = Array.isArray(features)
        ? features
              .map((feature, index) => ({ feature, index }))
              .filter(({ feature }) => feature && !isFeatureEmpty(feature))
        : [];
    const validFeatures = validFeaturesWithIndex.map(({ feature }) => feature);
    const isSingleItem = validFeatures.length === 1;

    const isLastItemAlone = validFeatures.length > 1 && validFeatures.length % 2 === 1;
    const gridFeatures = isLastItemAlone ? validFeatures.slice(0, -1) : validFeatures;
    const lastFeatureData = isLastItemAlone
        ? validFeaturesWithIndex[validFeaturesWithIndex.length - 1]
        : null;

    const renderFeature = (feature, index, shouldCenter = false) => {
        if (!feature || isFeatureEmpty(feature)) return null;

        const [button] = feature.callToAction || [];
        const showCallToAction = shouldShowCallToAction(button, true);
        const buttonUrl = button?.url || '#';

        const handleClick = (e) => {
            if (e.target.closest('a, button')) {
                return;
            }
            if (showCallToAction && buttonUrl && buttonUrl !== '#') {
                window.location.href = buttonUrl;
            }
        };

        const content = (
            <div
                key={feature.identifier || index}
                onClick={showCallToAction ? handleClick : undefined}
                className={cn(
                    bodyClasses,
                    'group !no-underline',
                    'p-[24px] transition-colors duration-300 ease-in-out',
                    'h-full w-full flex flex-col min-h-0',
                    showCallToAction && 'hover:cursor-pointer'
                )}>
                {feature.icon && (
                    <div className="mb-4 flex size-[56px] items-center justify-center rounded-full transition-colors duration-300 ease-in-out">
                        <Image
                            src={feature.icon?.idPath ?? feature.icon}
                            alt={feature.alt || 'image'}
                            height={56}
                            width={56}
                            sizes="56px"
                            className="size-[56px]"
                        />
                    </div>
                )}

                {feature.headline && (
                    <CustomHeading
                        level={3}
                        className="dot-card-header mt-0 text-2xl text-black dark:text-black">
                        {feature.headline}
                    </CustomHeading>
                )}

                {!feature.headline && <div className="h-[30px]"></div>}

                {feature.body ? (
                    <div className="paragraph-justify grow">
                        <DotBlockEditor
                            blocks={feature.body}
                            customRenderers={{
                                listItem: ListItem,
                                heading: Heading
                            }}
                        />
                    </div>
                ) : (
                    <div className="grow"></div>
                )}

                {showCallToAction &&
                    feature.callToAction?.map((cta, i) =>
                        cta ? (
                            <div key={(cta.identifier || '') + i} className="pt-1">
                                <LinkWithIcon
                                    className="btn !ml-0 mt-auto flex w-fit items-center justify-center gap-2 !border-0 !bg-transparent !px-0 !text-fuschia-700 transition-colors duration-300"
                                    url={cta.url}
                                    urlName={cta.urlName || cta.title}
                                    icon={cta.icon}
                                    iconPosition={cta.iconPosition || 'right'}
                                    ariaLabel={cta.ariaLabel || cta.urlName || cta.title}>
                                    {cta.urlName || cta.title}
                                </LinkWithIcon>
                            </div>
                        ) : null
                    )}
            </div>
        );

        return (
            <div
                key={feature.identifier || index}
                className={cn(
                    'flex h-full rounded-2xl bg-indigo-100',
                    shouldCenter ? 'w-full md:w-[calc((100%_-_2.5rem)_/_2)]' : 'w-full self-stretch'
                )}>
                {content}
            </div>
        );
    };

    return (
        <div
            className={cn(
                'prose relative max-w-none prose-h3:mt-2 prose-img:mb-0',
                'prose-a:no-underline prose-a:hover:no-underline'
            )}>
            {isSingleItem ? (
                <div className="relative flex justify-center pt-2">
                    {Array.isArray(features) &&
                        features.map((feature, index) => renderFeature(feature, index, true))}
                </div>
            ) : (
                <>
                    <div className="relative grid auto-rows-fr grid-cols-1 items-stretch gap-10 pt-2 md:grid-cols-2 lg:grid-cols-2">
                        {Array.isArray(features) &&
                            features.map((feature, index) => {
                                const validIndex = validFeatures.findIndex((f) => f === feature);
                                const isInGrid =
                                    validIndex >= 0 && validIndex < gridFeatures.length;
                                if (!isInGrid) return null;
                                return renderFeature(feature, index, false);
                            })}
                    </div>
                    {lastFeatureData && (
                        <div className="relative grid grid-cols-1 justify-items-center pt-10">
                            {renderFeature(lastFeatureData.feature, lastFeatureData.index, true)}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Layout10({ features }) {
    const headlineRefs = useRef([]);
    const bodyClasses = cn(
        'prose prose-p:text-black prose-p:opacity-80 prose-p:text-sm prose-p:text-justify prose-a:text-sm w-full max-w-none',
        'prose-a:text-black prose-a:opacity-80 prose-a:no-underline prose-a:hover:no-underline',
        'prose-li:text-black prose-li:opacity-80 prose-li:text-sm marker:text-black marker:opacity-80',
        'prose-p:max-w-none prose-p:w-full prose-ul:max-w-none prose-ul:w-full prose-ol:max-w-none prose-ol:w-full'
    );

    const validFeatures = useMemo(
        () =>
            Array.isArray(features)
                ? features.filter((feature) => feature && !isFeatureEmpty(feature))
                : [],
        [features]
    );

    useLayoutEffect(() => {
        const updateHeadlineHeights = () => {
            const validRefs = headlineRefs.current
                .slice(0, validFeatures.length)
                .filter((ref) => ref !== null && ref !== undefined);

            if (validRefs.length === 0 || validRefs.length !== validFeatures.length) {
                return;
            }

            const allHaveHeadings = validRefs.every((ref) => {
                const heading = ref.querySelector('h3');
                return heading && heading.offsetHeight > 0;
            });

            if (!allHaveHeadings) {
                return;
            }

            validRefs.forEach((ref) => {
                ref.style.height = 'auto';
                ref.style.minHeight = 'auto';
            });

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const heights = validRefs
                        .map((ref) => ref.scrollHeight)
                        .filter((height) => height > 0);
                    const maxHeight = heights.length > 0 ? Math.max(...heights) : 0;

                    if (maxHeight > 0) {
                        validRefs.forEach((ref) => {
                            ref.style.height = `${maxHeight}px`;
                            ref.style.minHeight = `${maxHeight}px`;
                        });
                    }
                });
            });
        };

        const timeoutDelays = [0];
        const timeoutIds = timeoutDelays.map((delay) => setTimeout(updateHeadlineHeights, delay));

        const handleResize = () => updateHeadlineHeights();
        window.addEventListener('resize', handleResize);

        return () => {
            timeoutIds.forEach((id) => clearTimeout(id));
            window.removeEventListener('resize', handleResize);
        };
    }, [validFeatures]);

    return (
        <div
            className={cn(
                'grid items-start justify-start',
                'sm:grid-cols-[repeat(auto-fit,minmax(17.5rem,1fr))]',
                'gap-8'
            )}>
            {validFeatures.map((feature, index) => {
                const [button] = feature.callToAction || [];
                const showCallToAction = shouldShowCallToAction(button, true);
                const buttonUrl = button?.url || '#';

                const handleClick = (e) => {
                    if (e.target.closest('a, button')) {
                        return;
                    }
                    if (showCallToAction && buttonUrl && buttonUrl !== '#') {
                        window.location.href = buttonUrl;
                    }
                };

                return (
                    <div
                        key={feature.identifier || index}
                        onClick={showCallToAction ? handleClick : undefined}
                        className={cn(
                            'prose max-w-none w-full flex flex-col',
                            showCallToAction && 'hover:cursor-pointer'
                        )}>
                        <div className="not-prose mb-6 inline-block size-16 rounded-full bg-slate-950/5 p-2 shadow-sm md:size-20 md:p-3">
                            <div className="relative flex size-full items-center justify-center">
                                <div className="absolute size-full rounded-full bg-fuschia-100"></div>
                                <span className="relative text-xl font-bold text-fuschia-700 md:text-2xl">
                                    {index + 1}
                                </span>
                            </div>
                        </div>

                        <div
                            ref={(el) => {
                                headlineRefs.current[index] = el;
                            }}
                            data-headline-container
                            className="min-h-0 w-full text-center">
                            {feature.headline && (
                                <CustomHeading
                                    level={3}
                                    className={cn(
                                        'dot-card-header mt-0 w-full !text-left text-md font-[600]',
                                        'md:text-2xl dark:text-white'
                                    )}>
                                    {feature.headline}
                                </CustomHeading>
                            )}
                        </div>

                        {feature.body && (
                            <div className={cn(bodyClasses, 'paragraph-justify')}>
                                <DotBlockEditor
                                    blocks={feature.body}
                                    customRenderers={{
                                        listItem: ListItem,
                                        heading: Heading
                                    }}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export function FeatureGrid(props) {
    const { layout, headline } = props;
    const layouts = {
        1: Layout1,
        2: Layout2,
        3: Layout3,
        4: Layout4,
        5: Layout5,
        6: Layout6,
        7: Layout7,
        8: Layout8,
        9: Layout9,
        10: Layout10
    };

    const styles = {
        1: 'container lg:!px-0 grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(23.33rem,1fr))] gap-10 relative items-stretch auto-rows-fr',
        2: 'container lg:!px-0 grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(17.5rem,1fr))] gap-10 relative',
        3: 'container lg:!px-0 grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(23.33rem,1fr))] gap-10 relative',
        4: 'container lg:!px-0 grid-cols-1 grid-rows-auto md:grid-cols-[repeat(2,1fr)] gap-4 lg:gap-6 relative',
        5: 'container lg:!px-0 color-grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(17.5rem,1fr))] gap-10 relative',
        6: 'container lg:!px-0 grid-cols-1 sm:grid-cols-3 gap-10 relative',
        7: 'container lg:!px-0 grid-cols-1 sm:grid-cols-3 gap-10 relative',
        8: 'container lg:!px-0 relative',
        9: 'container lg:!px-0 relative',
        10: 'container lg:!px-0 relative'
    };

    const Layout = layouts[layout] ?? Layout1;
    const style = styles[layout] ?? styles[1];

    if (
        (layout === 8 ||
            layout === '8' ||
            layout === 9 ||
            layout === '9' ||
            layout === 10 ||
            layout === '10') &&
        Array.isArray(props?.features) &&
        props.features.length > 0
    ) {
        const LayoutComponent = layouts[layout] ?? Layout1;
        const layoutProps =
            layout === 8 || layout === '8'
                ? { globalHeadline: headline, features: props.features }
                : { features: props.features };

        return (
            <div className={cn('grid', style)} data-component="feature-grid" data-layout={layout}>
                <LayoutComponent {...layoutProps} />
            </div>
        );
    }

    if (
        layout === 8 ||
        layout === '8' ||
        layout === 9 ||
        layout === '9' ||
        layout === 10 ||
        layout === '10'
    ) {
        return null;
    }

    return (
        <div className={cn('grid', style)} data-component="feature-grid" data-layout={layout}>
            {Array.isArray(props?.features) &&
                props.features.map((feature, index) => {
                    if (!feature) return null;
                    return layout === 1 || layout === '1' ? (
                        <div key={feature.identifier || `feature-${index}`} className="h-full">
                            <Layout {...feature} layout={layout} />
                        </div>
                    ) : (
                        <Layout
                            key={feature.identifier || `feature-${index}`}
                            {...feature}
                            layout={layout}
                        />
                    );
                })}
        </div>
    );
}
